import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Power, Terminal as TerminalIcon, Cpu, Globe, Activity, MessageSquare } from 'lucide-react';
import { fetchAtlasData, fetchEntryDetails, fetchArticles } from '../services/dataService';
import { fetchLiveNews } from '../services/geminiService';
import { AtlasEntry, BlogPost } from '../types';

type TerminalMode = 'COMMAND' | 'TICKET_SUBJECT' | 'TICKET_MESSAGE';

const TerminalSearch: React.FC = () => {
  const [power, setPower] = useState(false);
  const [booting, setBooting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [input, setInput] = useState('');
  const [output, setOutput] = useState<Array<{ type: 'cmd' | 'res' | 'err' | 'sys' | 'support' | 'data', content: React.ReactNode }>>([]);
  const [entries, setEntries] = useState<AtlasEntry[]>([]);
  
  // TICKET STATE
  const [mode, setMode] = useState<TerminalMode>('COMMAND');
  const [ticketData, setTicketData] = useState({ subject: '', message: '' });

  const monitorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // No navigation needed for content anymore, everything is internal
  const navigate = useNavigate();

  useEffect(() => {
    fetchAtlasData().then(data => setEntries(data));
  }, []);

  useEffect(() => {
    if (monitorRef.current) {
        monitorRef.current.scrollTop = monitorRef.current.scrollHeight;
    }
  }, [output, booting, power, processing, mode]);

  const handleTerminalClick = () => {
    if (power && !booting && !processing) {
        inputRef.current?.focus();
    }
  };

  const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // --- CONTENT RENDERERS (ASCII STYLE) ---

  const renderManifesto = () => (
      <div className="font-mono text-xs leading-relaxed whitespace-pre-wrap text-gray-800 my-4 border-l-2 border-black pl-4">
{`
=== CYBERUMANITÀ MANIFESTO v1.0 ===

1. L’UOMO PRIMA DELLA MACCHINA
   Crediamo che la tecnologia non debba sostituire l’essere umano, ma amplificarne le possibilità etiche, cognitive e creative.

2. L’ETICA COME INFRASTRUTTURA
   Non c’è progresso senza responsabilità. La dignità digitale e la trasparenza degli algoritmi sono le fondamenta.

3. LA CULTURA COME DIFESA
   In un’epoca di automazioni, la cultura resta il più potente antivirus dell’umanità.

4. IL DATO È MATERIA SPIRITUALE
   I dati non sono solo numeri: sono frammenti di esperienza umana. Trattarli con rispetto significa riconoscere che anche nell’informazione esiste una forma di anima.

[END OF FILE]
`}
      </div>
  );

  const renderContactInfo = () => (
      <div className="font-mono text-xs leading-relaxed text-gray-800 my-4">
{`
--- SECURE UPLINK ESTABLISHED ---

[CONTACT NODES]
> EMAIL PROTOCOL:   sysop@cyberumanita.com
> ENCRYPTION KEY:   PGP PUBLIC KEY A1B2... (See /pgp)
> RESPONSE TIME:    ~24h

[CONTRIBUTION]
To join the research network, please submit a ticket with subject "CONTRIBUTOR REQUEST".

[STATUS]
Uplink: ACTIVE
Signal: STRONG
`}
      </div>
  );

  const renderAtlasEntry = (detail: any) => (
      <div className="font-mono text-xs leading-relaxed text-gray-800 my-4 border border-black p-4 bg-gray-50">
          <div className="border-b border-black pb-2 mb-2 font-bold uppercase">
              ENTITY: {detail.header.title} [{detail.header.id}]
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
              <div>TYPE: {detail.header.type}</div>
              <div>RISK: {detail.header.riskCurrent.label} ({detail.header.riskCurrent.score}/5)</div>
          </div>
          <div className="mb-4">
              <span className="font-bold">{'>'}{'>'} DEFINITION:</span><br/>
              {detail.genesis.definition}
          </div>
          <div className="mb-4">
              <span className="font-bold">{'>'}{'>'} WORST CASE SCENARIO:</span><br/>
              {detail.overview.worstCaseScenario.description}<br/>
              <span className="bg-black text-white px-1">IMPACT: {detail.overview.worstCaseScenario.cost}</span>
          </div>
          <div className="mb-2">
              <span className="font-bold">{'>'}{'>'} OPS (MITRE):</span> {detail.ops.mitre.join(', ')}
          </div>
          <div className="text-[10px] text-gray-500 mt-4 pt-2 border-t border-gray-300">
              LAST UPDATE: {detail.header.lastUpdated} | OWNER: {detail.header.owner}
          </div>
      </div>
  );

  const handlePower = async () => {
      if (power) {
          setPower(false);
          setBooting(false);
          setOutput([]);
          setMode('COMMAND');
          return;
      }

      setPower(true);
      setBooting(true);
      
      const bootSteps = [
          "BIOS CHECK... OK",
          "MOUNTING FILESYSTEM... READ-ONLY",
          "LOADING TEXT RENDERER... OK",
          "RESTRICTING GRAPHICAL INTERFACE... DONE"
      ];

      for (const step of bootSteps) {
          await wait(200);
          setOutput(prev => [...prev, { type: 'sys', content: step }]);
      }

      await wait(500);
      setOutput([]); 
      
      setOutput(prev => [...prev, { 
          type: 'sys', 
          content: (
              <div className="my-4 font-sans border-y-2 border-black py-4 text-center">
                  <div className="text-3xl md:text-5xl font-black tracking-tight mb-2">CYBER.OS</div>
                  <div className="text-xs uppercase tracking-[0.5em] text-gray-500">Text-Only Interface</div>
                  <div className="mt-2 text-[10px] flex justify-center gap-4 text-gray-400 font-bold uppercase tracking-widest">
                      <span>MODE: TERMINAL</span>
                      <span>GUI: DISABLED</span>
                  </div>
              </div>
          ) 
      }]);
      
      await wait(200);
      setOutput(prev => [...prev, { 
          type: 'sys', 
          content: (
              <div className="mb-4 p-2 bg-gray-100 border-l-4 border-black text-xs font-sans font-medium">
                  <div>SYSTEM READY.</div>
                  <div>Terminale operativo per ricerche interne al database.</div>
                  <div>Scrivi <span className="font-bold">'help'</span> per visualizzare i comandi disponibili.</div>
              </div>
          ) 
      }]);
      
      setBooting(false);
      setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleInputSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || processing) return;

    const rawInput = input.trim();
    
    // --- MODE: TICKET FLOW ---
    if (mode === 'TICKET_SUBJECT') {
        setOutput(prev => [...prev, { type: 'cmd', content: rawInput }]);
        setTicketData(prev => ({ ...prev, subject: rawInput }));
        setMode('TICKET_MESSAGE');
        setOutput(prev => [...prev, { type: 'support', content: "DESCRIBE REQUEST / ERROR DETAILS:" }]);
        setInput('');
        return;
    }

    if (mode === 'TICKET_MESSAGE') {
        setOutput(prev => [...prev, { type: 'cmd', content: rawInput }]);
        setProcessing(true);
        
        const finalMessage = rawInput;
        const ticketId = Math.floor(Math.random() * 90000) + 10000;
        
        // Simula Handshake SMTP
        setOutput(prev => [...prev, { type: 'sys', content: "ENCRYPTING PAYLOAD..." }]);
        await wait(600);
        setOutput(prev => [...prev, { type: 'sys', content: "RESOLVING HOST: noreply@cyberumanita.com..." }]);
        await wait(600);
        
        const subject = `[TICKET #${ticketId}] ${ticketData.subject}`;
        const body = `TICKET ID: ${ticketId}\nDATE: ${new Date().toISOString()}\n\nMESSAGE:\n${finalMessage}\n\n---\nSent via Cyberumanità Terminal`;
        const mailtoLink = `mailto:noreply@cyberumanita.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        
        window.location.href = mailtoLink;

        setOutput(prev => [...prev, { type: 'sys', content: "HANDSHAKE ESTABLISHED. OPENING MAIL CLIENT..." }]);
        await wait(800);
        
        setOutput(prev => [...prev, { 
            type: 'res', 
            content: (
                <div className="bg-black text-white p-2 text-xs font-bold uppercase tracking-wider my-2 border-l-4 border-green-500">
                    ✓ TICKET #{ticketId} GENERATED.<br/>
                    TARGET: noreply@cyberumanita.com<br/>
                    STATUS: AWAITING USER CONFIRMATION (CHECK MAIL APP).
                </div>
            )
        }]);

        setTicketData({ subject: '', message: '' });
        setMode('COMMAND');
        setProcessing(false);
        setInput('');
        return;
    }

    // --- MODE: STANDARD COMMANDS ---
    const cmd = rawInput.toLowerCase();
    const parts = cmd.split(' ');
    const mainCmd = parts[0];
    const arg = parts.slice(1).join(' ');

    setOutput(prev => [...prev, { type: 'cmd', content: rawInput }]);
    setInput('');

    // --- 1. FIREWALL ---
    const forbiddenTerms = ['exit', 'quit', 'logout', 'google', 'facebook', 'instagram', 'twitter', 'http', 'www'];
    if (forbiddenTerms.some(term => cmd.includes(term))) {
        setOutput(prev => [...prev, { 
            type: 'err', 
            content: "SECURITY ALERT: EXTERNAL CONNECTION BLOCKED. STAY IN THE MATRIX."
        }]);
        return;
    }

    // --- 2. TEXT CONTENT COMMANDS ---
    
    // MANIFESTO
    if (mainCmd === 'manifesto' || (mainCmd === 'cat' && arg === 'manifesto')) {
        setOutput(prev => [...prev, { type: 'data', content: renderManifesto() }]);
    }
    // CONTACTS
    else if (mainCmd === 'contatti' || mainCmd === 'contacts' || (mainCmd === 'cat' && arg === 'contatti')) {
        setOutput(prev => [...prev, { type: 'data', content: renderContactInfo() }]);
    }
    // NEWS / ARTICLES
    else if (mainCmd === 'news' || mainCmd === 'articoli' || mainCmd === 'ls' && arg === 'news') {
        setProcessing(true);
        const articles = await fetchArticles();
        setProcessing(false);
        setOutput(prev => [...prev, { 
            type: 'res', 
            content: (
                <div className="my-2 font-mono text-xs">
                    <div className="mb-2 font-bold bg-black text-white px-1 inline-block">LATEST INTEL REPORTS:</div>
                    {articles.map(a => (
                        <div key={a.id} className="mb-1">
                            <span className="text-gray-500">[{a.id}]</span> <span className="font-bold">{a.title}</span> - <span className="italic">{a.author}</span>
                        </div>
                    ))}
                    <div className="mt-2 text-gray-500">Type 'read [id]' to open report.</div>
                </div>
            )
        }]);
    }
    // READ ARTICLE SPECIFIC
    else if (mainCmd === 'read' && !isNaN(Number(arg))) {
         setProcessing(true);
         // Mock fetching specific article logic or filter from fetched list
         const articles = await fetchArticles();
         const art = articles.find(a => a.id === Number(arg));
         setProcessing(false);

         if (art) {
             // Strip HTML simple for terminal
             const tempDiv = document.createElement("div");
             tempDiv.innerHTML = art.content;
             const textContent = tempDiv.textContent || tempDiv.innerText || "";
             
             setOutput(prev => [...prev, { 
                 type: 'data', 
                 content: (
                     <div className="font-mono text-xs leading-relaxed my-4 p-2 bg-gray-50 border-l-2 border-green-600 text-gray-900">
                         <div className="font-bold text-lg mb-2 uppercase">{art.title}</div>
                         <div className="mb-4 text-gray-500">AUTHOR: {art.author} | DATE: {art.date}</div>
                         <div className="whitespace-pre-wrap">{textContent.slice(0, 1500)}... [BUFFER LIMIT]</div>
                         <div className="mt-2 bg-black text-white inline-block px-1">END OF STREAM</div>
                     </div>
                 )
             }]);
         } else {
             setOutput(prev => [...prev, { type: 'err', content: `FILE ID ${arg} NOT FOUND.` }]);
         }
    }
    // READ ATLAS ENTRY
    else if (mainCmd === 'view' || mainCmd === 'read' || mainCmd === 'cat') {
        if (!arg) {
            setOutput(prev => [...prev, { type: 'err', content: "SPECIFY TARGET. EXAMPLE: 'view phishing'" }]);
            return;
        }
        setProcessing(true);
        const detail = await fetchEntryDetails(arg);
        setProcessing(false);
        
        // Check if it's a real entry (not auto-gen fallback unless explicitly allowed)
        if (detail) {
             setOutput(prev => [...prev, { type: 'data', content: renderAtlasEntry(detail) }]);
        } else {
             setOutput(prev => [...prev, { type: 'err', content: "ENTRY NOT FOUND IN LOCAL DB." }]);
        }
    }
    // HELP / LS
    else if (mainCmd === 'help' || mainCmd === 'ls' || mainCmd === 'dir') {
        setOutput(prev => [...prev, {
            type: 'res',
            content: (
                <div className="grid grid-cols-1 gap-y-2 text-black my-2 font-sans text-xs">
                    <div className="border-b-2 border-black pb-1 mb-2 font-bold uppercase tracking-widest flex justify-between">
                        <span>ROOT DIRECTORY</span>
                        <span>READ-ONLY</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <div className="text-gray-400 text-[9px] uppercase tracking-widest mb-1">Text Files</div>
                            <div className="flex flex-col gap-1 font-mono font-bold text-[10px]">
                                <span>manifesto.txt</span>
                                <span>contatti.txt</span>
                                <span>privacy_policy.txt</span>
                            </div>
                        </div>
                        <div>
                            <div className="text-gray-400 text-[9px] uppercase tracking-widest mb-1">Executables</div>
                            <div className="flex flex-col gap-1 font-mono text-[10px]">
                                <span>search [term]</span> (Cerca nel DB)
                                <span>view [term]</span> (Leggi Scheda)
                                <span>news</span> (Ultime News)
                                <span>read [id]</span> (Leggi Articolo)
                                <span>ticket</span> (Assistenza)
                            </div>
                        </div>
                    </div>
                </div>
            )
        }]);
    } 
    else if (mainCmd === 'clear') {
        setOutput([]);
    }
    else if (mainCmd === 'off') {
        handlePower();
    }
    else if (mainCmd === 'ticket') {
        setMode('TICKET_SUBJECT');
        setOutput(prev => [...prev, { 
            type: 'support', 
            content: (
                <div className="text-black font-bold uppercase tracking-wider bg-gray-200 px-2 py-1 inline-block">
                    --- CUSTOMER SERVICE PROTOCOL INITIATED ---
                </div>
            ) 
        }]);
        setTimeout(() => setOutput(prev => [...prev, { type: 'support', content: "ENTER TICKET SUBJECT (e.g., Missing Entry, Correction):" }]), 100);
    }
    // --- STRICT SEARCH LOGIC ---
    else if (mainCmd === 'search' || !cmd.includes(' ')) {
        // Allow implicit search if typing a single word or if command starts with search
        const query = mainCmd === 'search' ? arg : cmd;
        
        if (!query) return;

        setProcessing(true);
        await wait(400);

        const results = entries.filter(e => 
            e.title.toLowerCase().includes(query) || 
            e.tags.some(t => t.toLowerCase().includes(query))
        );

        if (results.length > 0) {
            setOutput(prev => [...prev, {
                type: 'res',
                content: (
                    <div className="my-2 font-sans space-y-3">
                        <div className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">Database Match Found:</div>
                        {results.slice(0, 3).map((item, i) => (
                            <div key={i} className="bg-gray-50 border border-gray-300 p-2 hover:border-black transition-colors">
                                <div className="font-bold text-sm uppercase">{item.title}</div>
                                <div className="text-[10px] text-gray-500 flex gap-2 mb-1">
                                    <span className="bg-gray-200 px-1 rounded font-bold text-black">{item.category}</span>
                                </div>
                                <div className="text-xs text-gray-600 font-serif italic truncate">{item.shortDescription}</div>
                            </div>
                        ))}
                        <div className="mt-1 text-[9px] text-gray-400">
                            {'>'} Type <span className="font-bold text-black font-mono">view {query}</span> to read full data here.
                        </div>
                    </div>
                )
            }]);
        } else {
            setOutput(prev => [...prev, { 
                type: 'err', 
                content: (
                    <div>
                        <div className="font-bold">ERROR: ENTITY_NOT_FOUND</div>
                        <div className="text-[10px] font-normal opacity-80 mt-1">
                            The term "{query.toUpperCase()}" does not exist in the local Atlas database.
                        </div>
                        <div className="mt-2 text-black bg-white border border-gray-300 p-2 text-[10px] font-medium">
                            Do you want to request this entry?
                            <br/>Type <span className="font-bold bg-gray-200 px-1">ticket</span> to send a request to the content team.
                        </div>
                    </div>
                ) 
            }]);
        }
        setProcessing(false);
    } 
    else {
        setOutput(prev => [...prev, { type: 'err', content: `UNKNOWN COMMAND '${cmd}'. TYPE 'help' OR 'ls'.` }]);
    }
    
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  return (
    <div className="w-full h-full flex flex-col bg-transparent relative font-sans">
        
        {/* POWER BUTTON */}
        {!power && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm z-20 rounded-lg">
                <button 
                    onClick={handlePower}
                    className="group flex flex-col items-center gap-3 transition-transform active:scale-95"
                >
                    <div className="w-16 h-16 rounded-full bg-white border-4 border-black shadow-[4px_4px_0px_rgba(0,0,0,1)] flex items-center justify-center group-hover:bg-black group-hover:border-black transition-colors">
                        <Power size={24} className="text-black group-hover:text-white transition-colors" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-black group-hover:text-black font-sans bg-white px-2">Boot Search Engine</span>
                </button>
            </div>
        )}

        {/* TERMINAL WINDOW */}
        <div className="bg-white rounded-lg shadow-[8px_8px_0px_rgba(0,0,0,0.1)] overflow-hidden border-2 border-black flex flex-col h-full">
            
            {/* HEADER */}
            <div className="bg-gray-100 px-4 py-3 flex items-center justify-between border-b-2 border-black shrink-0">
                <div className="flex gap-2">
                    <div className={`w-3 h-3 rounded-full border border-black ${power ? 'bg-green-500' : 'bg-white'}`}></div>
                    <div className="w-3 h-3 rounded-full border border-black bg-white"></div>
                    <div className="w-3 h-3 rounded-full border border-black bg-white"></div>
                </div>
                <div className="text-[10px] font-sans text-black font-bold flex items-center gap-2 uppercase tracking-widest">
                    <TerminalIcon size={12} />
                    CYBER.OS SEARCH
                </div>
                <div className="flex gap-2 text-[9px] font-bold text-gray-400">
                    <div className={`flex items-center gap-1 ${mode !== 'COMMAND' ? 'text-green-600 animate-pulse' : ''}`}>
                         {mode !== 'COMMAND' ? 'SUPPORT_MODE' : 'IDLE'}
                    </div>
                </div>
            </div>

            {/* MONITOR AREA */}
            <div 
                ref={monitorRef}
                className="flex-1 bg-white relative overflow-hidden flex flex-col"
                onClick={handleTerminalClick}
            >
                {/* Paper Texture */}
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/paper.png')] opacity-50 pointer-events-none"></div>

                {!power && <div className="absolute inset-0 flex items-center justify-center text-gray-300 font-bold uppercase tracking-widest text-xs font-sans z-0">System Offline</div>}

                <div className="relative z-10 h-full overflow-y-auto p-6 font-sans text-sm scrollbar-hide text-black">
                    {output.map((line, i) => (
                        <div key={i} className="mb-2 break-words animate-fade-in leading-relaxed font-sans">
                            {line.type === 'cmd' && <span className="text-gray-400 mr-2 font-bold select-none">{'>'}</span>}
                            {line.type === 'sys' && <span className="text-black mr-2 font-bold text-[10px] bg-gray-200 px-1 uppercase tracking-wide">[SYS]</span>}
                            {line.type === 'err' && <span className="text-white bg-red-600 px-1 mr-2 font-bold text-[10px] uppercase tracking-wide">[ERR]</span>}
                            {line.type === 'support' && <span className="text-white bg-blue-600 px-1 mr-2 font-bold text-[10px] uppercase tracking-wide">[TICKET]</span>}
                            
                            <span className={
                                line.type === 'err' ? 'text-red-600 font-bold' : 
                                line.type === 'cmd' ? 'text-black font-bold' :
                                line.type === 'support' ? 'text-blue-800 font-bold' :
                                'text-gray-800'
                            }>{line.content}</span>
                        </div>
                    ))}
                    
                    {power && !booting && !processing && (
                        <form onSubmit={handleInputSubmit} className="flex items-center gap-2 mt-4 border-t border-dashed border-gray-300 pt-2">
                            <span className={`text-black font-bold animate-pulse font-sans select-none ${mode !== 'COMMAND' ? 'text-blue-600' : ''}`}>
                                {mode === 'COMMAND' ? '>' : mode === 'TICKET_SUBJECT' ? 'SUBJECT >' : 'MSG >'}
                            </span>
                            <input
                                ref={inputRef}
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                className="flex-1 bg-transparent border-none outline-none text-black font-bold caret-black p-0 focus:ring-0 placeholder-transparent font-sans text-sm"
                                autoFocus
                                spellCheck={false}
                                autoComplete="off"
                                placeholder={mode === 'TICKET_MESSAGE' ? "Type message..." : ""}
                            />
                        </form>
                    )}
                    {processing && (
                        <div className="mt-2 text-gray-400 animate-pulse text-[10px] font-sans font-bold uppercase tracking-widest">_ PROCESSING...</div>
                    )}
                </div>
            </div>
        </div>
    </div>
  );
};

export default TerminalSearch;